import { useCallback, useEffect, useRef, useState } from 'react';

/** ~2s: time to read "Copied" (or the failure notice) and return to the normal state. */
export const COPY_FEEDBACK_MS = 2000;

export type CopyFeedbackStatus = 'idle' | 'copied' | 'failed';

/** Writes text to the clipboard; resolves `true` when some path copied, never rejects. The
 *  host owns it (the iframe-safe `copyText` of the chat, for one). */
export type WriteClipboard = (text: string) => Promise<boolean>;

/**
 * Copy WITH visible feedback (#2976): without it, copying and copying nothing are the same
 * screen, and the button becomes "the button that does not work".
 *
 * `copy` calls `write` synchronously inside the gesture (the legacy `execCommand` path needs
 * transient activation) and only marks `copied` when a path really copied. The state returns to
 * `idle` after `COPY_FEEDBACK_MS`; a new click restarts the clock. Each click is a numbered
 * attempt: an old FAILURE resolving after a newer success must not paint "could not copy" — only
 * the newest attempt counts, and an attempt resolving after unmount sets nothing.
 *
 * `onFailed` runs only for the failure the screen SHOWS (the host's telemetry): a stale attempt
 * and an unmounted component do not report.
 */
export function useCopyFeedback(
  write: WriteClipboard,
  onFailed?: () => void,
): {
  status: CopyFeedbackStatus;
  copy: (text: string) => void;
} {
  const [status, setStatus] = useState<CopyFeedbackStatus>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attempt = useRef(0);
  // Re-armed in the effect setup because of StrictMode's mount→unmount→mount (dev), which runs
  // the cleanup without changing instance/ref.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, []);
  const copy = useCallback(
    (text: string) => {
      const attemptId = ++attempt.current;
      // The previous attempt's clock dies here: the new one owns the state from now on.
      if (timer.current !== null) {
        clearTimeout(timer.current);
        timer.current = null;
      }
      // `write` never rejects (best-effort by contract), so `then` is enough.
      void write(text).then((ok) => {
        if (!alive.current || attemptId !== attempt.current) return;
        if (!ok) {
          // Reporting is best-effort: a throwing reporter cannot silence the notice on screen.
          try {
            onFailed?.();
          } catch {
            /* never blocks the feedback */
          }
        }
        setStatus(ok ? 'copied' : 'failed');
        timer.current = setTimeout(() => {
          timer.current = null;
          setStatus('idle');
        }, COPY_FEEDBACK_MS);
      });
    },
    [write, onFailed],
  );
  return { status, copy };
}
